/**
 * Manages the dispensary-specific GA v4 client lifecycle.
 *
 * Starts with a delayed-call proxy that buffers events until the dispensary's
 * GA measurement ID is known, then replays them on the real client.
 */
import { createGaV4Client, createGaV4DelayedCallClient, GaV4Client } from 'utils/analytics/clients/ga-v4-client';
import { isGaV4Id } from 'utils/analytics/helpers/is-ga-v4-id';

/** GaV4Client extended with the updateClient method from the delayed-call proxy. */
type DelayedGaV4Client = GaV4Client & { updateClient?: (newClient: GaV4Client) => void };

// Start with a delayed call client until we have access to the dispensary's GA ID
let dispensaryGaV4Client: DelayedGaV4Client = createGaV4DelayedCallClient();
let initialized = false;

/** Read-only accessor for the current dispensary GA v4 client. */
export const getDispensaryGaV4Client = (): GaV4Client => dispensaryGaV4Client;

/**
 * Initializes the dispensary GA v4 client with the given measurement ID.
 * Replays any events that were buffered before initialization.
 *
 * Only acts on GA v4 IDs (prefixed with "G-"). Non-GA4 IDs are ignored.
 * Only initializes once per page lifecycle (mirrors the original _once() guard).
 */
export function initDispensaryGaV4Tracker(id: string): void {
  if (initialized || !isGaV4Id(id)) {
    return;
  }
  initialized = true;

  const delayedCallClient = dispensaryGaV4Client;
  dispensaryGaV4Client = createGaV4Client(id);

  // Replay all the events that were captured before the client was initialized
  delayedCallClient?.updateClient?.(dispensaryGaV4Client);
}
